import React from 'react';
import { Text, ScrollView, View, TextInput, Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { SuccessModal } from '@double_point/rn-components';
import WalletScreenProps from 'types/screens/WalletScreenProps';
import { styles } from 'styles/screens/refundsScreen';
import AppContext from 'contexts/AppContext';
import OrderStatuses from 'types/enums/OrderStatuses';
import Order from 'types/models/Order';
import { getOrder, refundOrder, sendInvoice } from 'services/ordersService';
import Commerce from 'types/models/Commerce';
import refundsOrderForm, { refundEmailForm } from 'forms/schemas/refundsForm';
import UserRoleTypes from 'types/enums/UserRoleTypes';
import { Button } from 'react-native-elements';
import RefundAuthorizationCodeVerification from 'components/RefundAuthorizationCodeVerification';
import { valueToCurrencyFormat } from 'utils/parseCurrency';

export default ({}: WalletScreenProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { currentAuthData, setModalizeData, setIsLoading, currentVenue } = React.useContext(AppContext);
  const [currentCommerce, setCurrentCommerce] = React.useState<Commerce | null>(null);
  const [order, setOrder] = React.useState<Order | null>(null);
  const [showVerificationCodeModal, setShowVerificationCodeModal] = React.useState<boolean>(false);
  const [showEmailInput, setShowEmailInput] = React.useState<boolean>(false);

  const findUserCommerce = (commerceId: string | undefined) => {
    if (!currentVenue || !commerceId) return;
    const userCommerce = currentVenue.commerces.find((commerce: Commerce) => commerce.id === commerceId);
    if (userCommerce) {
      setCurrentCommerce(userCommerce);
    }
  };

  const formik = useFormik({
    initialValues: {
      orderId: '',
    },
    onSubmit: () => onConfirm(),
    validationSchema: refundsOrderForm(),
    validateOnBlur: false,
    validateOnChange: false,
  });

  const formikEmail = useFormik({
    initialValues: {
      email: '',
    },
    onSubmit: () => onConfirmInvoice(),
    validationSchema: refundEmailForm(),
    validateOnBlur: false,
    validateOnChange: false,
  });

  const onConfirm = async () => {
    const orderId = parseInt(formik.values.orderId);
    setOrder(null);
    setIsLoading(true);
    try {
      const fullOrder = await getOrder(orderId);
      console.log(fullOrder);
      if (!fullOrder) {
        setModalizeData({
          content: ModalMessage({
            success: false,
            message: t('not-exists-order-id'),
            title: t('warning-title'),
            buttonText: t('try-another-order'),
          }),
          closeOnOverlayTap: true,
        });
        return;
      }

      let orderBelongingCheck = false;
      const commerceIds = currentVenue?.commerces.map((commerce) => commerce.id);
      // Al ser editor de venue verificamos que la orden encontrada pertenezca a alguno de mis comercios
      if (currentAuthData?.user.roleId === UserRoleTypes.VenueEditor)
        orderBelongingCheck = !commerceIds?.includes(fullOrder.commerce.id);
      // Al ser editor de comercio verificamos que la orden encontrada pertenezca a nuestro
      // ya que tenemos seteado el currentCommerce
      if (currentAuthData?.user.roleId === UserRoleTypes.CommerceEditor)
        orderBelongingCheck = fullOrder.commerce.id !== currentCommerce?.id;

      if (orderBelongingCheck) {
        if (!commerceIds?.includes(fullOrder.commerce.id)) {
          setModalizeData({
            content: ModalMessage({
              success: false,
              message: t('non-belonging-order-message'),
              title: t('warning-title'),
              buttonText: t('try-another-order'),
            }),
            closeOnOverlayTap: true,
          });
          return;
        }
      }

      setOrder(fullOrder);
      if (!currentCommerce) {
        console.log('entro a findUserCommerce');
        findUserCommerce(fullOrder?.commerce.id);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const ModalMessage = ({ success = false, message = '', title = '', buttonText = '' }): JSX.Element => (
    <SuccessModal
      isSuccess={success}
      title={title}
      mainColor={currentVenue?.appColor ?? '#001C47'}
      message={message}
      buttonText={buttonText}
      onConfirm={() => {
        setModalizeData(null);
        formik.resetForm();
      }}
    />
  );

  const refundsButtonIsDisabled = (order: Order): boolean => {
    // Se deshabilita el botón si:
    // supera las 48h
    // Está en estado creado o incompleto
    // ya fue devuelto
    if (outOfDateOrder(order.localDateTime) || orderWithInvalidStatus(order.orderStatusId) || order.refund) return true;
    return false;
  };

  const outOfDateOrder = (date: string) => {
    const parsedDate = new Date(date);
    const now = new Date();
    // Calcular la diferencia en milisegundos
    const diferenciaMilisegundos = now.getTime() - parsedDate.getTime();
    // Calcular la diferencia en horas
    const diferenciaHoras = diferenciaMilisegundos / (1000 * 60 * 60);
    return diferenciaHoras > 48;
  };

  const orderWithInvalidStatus = (status: number): boolean => {
    const invalidStatuses = [OrderStatuses.created, OrderStatuses.incompleted];
    return invalidStatuses.includes(status);
  };

  const handleRefundOrder = async () => {
    setIsLoading(true);
    try {
      const body = { id: order?.id };
      const response = await refundOrder(body);
      const success = response.result;
      const message = success ? t('order-refund-success') : t('order-refund-error');
      const title = t('refund');
      const buttonText = success ? t('accept') : t('try-again');

      const content = ModalMessage({ message, title, success, buttonText });
      setModalizeData({ content, closeOnOverlayTap: true });
      if (success === true) setOrder(null);
    } catch (error) {
      const content = ModalMessage({
        message: t('order-refund-error'),
        title: t('refund'),
        success: false,
        buttonText: t('try-again'),
      });
      setModalizeData({ content, closeOnOverlayTap: true });
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  };

  const onConfirmInvoice = async () => {
    setIsLoading(true);
    try {
      const body = { id: order?.id, email: formikEmail.values.email };
      const response = await sendInvoice(body);
      const success = response.result;
      const message = success ? t('order-invoice-sent') : t('error-occurred');
      const title = t('invoice');

      const content = ModalMessage({ message, title, success, buttonText: t('accept') });
      setModalizeData({ content, closeOnOverlayTap: true });
    } catch (error) {
      const content = ModalMessage({
        message: t('error-occurred'),
        title: t('invoice'),
        success: false,
        buttonText: t('accept'),
      });
      setModalizeData({ content, closeOnOverlayTap: true });
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlSendInvoice = async () => {
    console.log('test');
    if (order?.payment?.cloverPointPaymentId) {
      setShowEmailInput(!showEmailInput);
    } else {
      onConfirmInvoice();
    }
  };

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <View style={styles.upperTextContainerLarge}>
          <Text style={styles.textTitleLarge}>{t('refunds')}</Text>
        </View>
      </View>

      <ScrollView style={styles.scrollItemsContainer} contentContainerStyle={styles.scrollItemsContentContainer}>
        <>
          <View style={{ alignItems: 'center' }}>
            <View style={[styles.descriptionContainer]}>
              <View style={styles.checkboxContainer}>
                <Text style={styles.descriptionText}>{t('search-order-for-detail')}</Text>
              </View>
            </View>
          </View>

          <View style={{ alignItems: 'center' }}>
            <>
              <TextInput
                keyboardType="number-pad"
                style={[styles.textInput]}
                onChangeText={formik.handleChange('orderId')}
                placeholder={t('order-id-placeholder')}
                value={formik.values.orderId}
                maxLength={7}
                placeholderTextColor={'#959393'}
              ></TextInput>
              {formik.errors.orderId && formik.errors.orderId.length > 0 && (
                <View style={styles.errorTextContainer}>
                  <Text style={styles.errorText}>{formik.errors.orderId}</Text>
                </View>
              )}
            </>
          </View>

          <Button
            title={t('search')}
            type="clear"
            containerStyle={styles.confirmPaymentBtnContainer}
            buttonStyle={[styles.confirmPaymentBtn, { backgroundColor: currentVenue?.appColor ?? '#056EC1' }]}
            titleStyle={[styles.confirmPaymentBtnText, { color: '#FFF' }]}
            disabled={formik.isSubmitting}
            disabledStyle={[styles.confirmPaymentBtn, styles.confirmPaymentBtnDisabled]}
            disabledTitleStyle={styles.confirmPaymentBtnText}
            onPress={() => formik.handleSubmit()}
          />
        </>

        {order && (
          <>
            <View>
              {order.details?.map((item) => (
                <View key={item.id} style={styles.itemDetailContainer}>
                  <View style={styles.flex1}>
                    <Text>
                      <Text style={styles.fontWeightBold}>• </Text>
                      {item.product.name}
                    </Text>
                  </View>
                  <View style={styles.itemDataContainer}>
                    <View style={styles.flex1}>
                      <Text>
                        <Text style={styles.fontWeightBold}>{t('price')}: </Text>
                        {valueToCurrencyFormat(item.price, i18n.language)}
                      </Text>
                    </View>
                    <View style={styles.flex1}>
                      <Text>
                        <Text style={styles.fontWeightBold}>{t('quantity')}: </Text>
                        {item.quantity}
                      </Text>
                    </View>
                    <View style={styles.flex1}>
                      <Text>
                        <Text style={styles.fontWeightBold}>{t('total')}: </Text>
                        {valueToCurrencyFormat(parseFloat(item.price) * item.quantity, i18n.language)}
                      </Text>
                    </View>
                  </View>
                </View>
              ))}
              <View style={[styles.flex1, styles.mt16]}>
                <Text>
                  <Text style={styles.fontWeightBold}>{t('total-amount')}: </Text>
                  {valueToCurrencyFormat(order.totalAmount, i18n.language)}
                </Text>
              </View>
            </View>

            <View style={styles.mt16}>
              <Pressable style={styles.topRowOrderItemOnOrdersListLarge} onPress={handlSendInvoice}>
                <View>
                  <Text style={styles.topRowText}>{t('send-invoice')}</Text>
                </View>
              </Pressable>

              {showEmailInput && (
                <>
                  <TextInput
                    keyboardType="email-address"
                    style={[styles.textInput]}
                    onChangeText={formikEmail.handleChange('email')}
                    placeholder={'Email'}
                    value={formikEmail.values.email}
                    placeholderTextColor={'#959393'}
                  ></TextInput>
                  {formikEmail.errors.email && formikEmail.errors.email.length > 0 && (
                    <View style={styles.errorTextContainer}>
                      <Text style={styles.errorText}>{formikEmail.errors.email}</Text>
                    </View>
                  )}

                  <Button
                    onPress={() => formikEmail.submitForm()}
                    title={t('confirm-button-text')}
                    type="solid"
                    buttonStyle={styles.button}
                    titleStyle={styles.buttonTitle}
                    containerStyle={styles.buttonContainerLarge}
                    disabled={formikEmail.isSubmitting}
                  />
                </>
              )}

              <View style={styles.mt16}>
                <Pressable
                  style={[
                    styles.topRowOrderItemOnOrdersListLarge,
                    refundsButtonIsDisabled(order) && styles.disableButton,
                  ]}
                  disabled={refundsButtonIsDisabled(order)}
                  onPress={() => setShowVerificationCodeModal(true)}
                >
                  <View>
                    <Text style={styles.topRowText}>{t('refund-order')}</Text>
                  </View>
                </Pressable>
                <View style={styles.mt16}>
                  {refundsButtonIsDisabled(order) && <Text>{t('not-available-to-refund-title')}:</Text>}
                  {order.refund && (
                    <>
                      <Text style={styles.reasons}>• {t('order-already-refund')}</Text>
                    </>
                  )}
                  {outOfDateOrder(order.localDateTime) && (
                    <>
                      <Text style={styles.reasons}>• {t('order-out-of-date-to-refund')}</Text>
                    </>
                  )}
                  {orderWithInvalidStatus(order.orderStatusId) && (
                    <>
                      <Text style={styles.reasons}>• {t('order-with-status-invalid-to-refund')}</Text>
                    </>
                  )}
                </View>
              </View>
            </View>
          </>
        )}
      </ScrollView>
      {showVerificationCodeModal && (
        <RefundAuthorizationCodeVerification
          title={t('enter-verification-code')}
          visible={showVerificationCodeModal}
          setVisible={setShowVerificationCodeModal}
          onHandleModal={() => {
            setShowVerificationCodeModal(false);
            handleRefundOrder();
          }}
          currentCommerce={currentCommerce}
          generalColor={currentVenue?.appColor ?? '#001C47'}
        />
      )}
    </ScrollView>
  );
};
